import { useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { Anchor, Text, Title } from '@mantine/core'
import { IconAlertTriangle, IconChevronLeft } from '@tabler/icons-react'
import { AuthLoginStep } from 'auth/models'
import { setIsLoginPageUiCenter, setLoginStep } from 'auth/store'
import clsx from 'clsx'
import { useDispatch } from 'common/hooks'
import { RoutesEnum } from 'services'

import classes from './../components/components.module.css'

export default function SSOErrorPage() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()

  // Parse URL parameters
  const error = searchParams.get('error')
  const errorDescription = searchParams.get('error_description')
  const organization = searchParams.get('organization')
  const email = searchParams.get('email')

  // Determine error message based on error type
  const errorTitle = useMemo(() => {
    switch (error) {
      case 'invalid_organization':
        return t('auth.ssoError.invalidOrganization.title')
      case 'access_denied':
        return t('auth.ssoError.accessDenied.title')
      case 'server_error':
        return t('auth.ssoError.serverError.title')
      default:
        return t('auth.ssoError.general.title')
    }
  }, [error, t])

  const errorMessage = useMemo(() => {
    switch (error) {
      case 'invalid_organization':
        return t('auth.ssoError.invalidOrganization.message', { organization })
      case 'access_denied':
        return t('auth.ssoError.accessDenied.message', { email })
      case 'server_error':
        return t('auth.ssoError.serverError.message')
      default:
        return t('auth.ssoError.general.message')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, errorDescription, organization, t, email])

  const handleBackToLogin = () => {
    dispatch(setLoginStep({ step: AuthLoginStep.SIGN_IN }))
  }

  useEffect(() => {
    dispatch(setIsLoginPageUiCenter({ isLoginPageUiCenter: true }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Helmet>
        <title>{t('auth.ssoError.pageTitle')}</title>
      </Helmet>
      <div className={'flex flex-col justify-start w-full max-w-[600px] mx-auto'}>
        <Anchor
          className={clsx(
            'mb-6 md:mb-8 flex items-center gap-2 font-medium no-underline focus:no-underline focus:outline-none',
            classes.loginFormForgotPasswordLink,
          )}
          component={Link}
          onClick={handleBackToLogin}
          to={RoutesEnum.LOGIN}
        >
          <IconChevronLeft size={16} />
          {t('auth.control.goBack')}
        </Anchor>

        <div className={'flex flex-col items-center text-center'}>
          <div
            className={clsx(
              'w-20 h-20 rounded-full flex items-center justify-center mb-6',
              'bg-red-100 text-red-600',
            )}
          >
            <IconAlertTriangle size={40} />
          </div>

          <Title
            className={clsx('font-medium mb-4 text-center', classes.loginFormTitleH2)}
            order={2}
          >
            {errorTitle}
          </Title>

          <Text className={'mb-6 text-gray-600 max-w-[500px]'} size={'md'}>
            {errorMessage}
          </Text>

          {organization && (
            <div className={'mb-6 p-4 bg-gray-50 rounded-lg'}>
              <Text className={'text-gray-500'} size={'sm'}>
                {t('auth.ssoError.organizationLabel')}
              </Text>
              <Text className={'font-semibold'} size={'md'}>
                {organization}
              </Text>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
